import { createTheme } from '@mui/material/styles';

export const COLOR_MODE_STORAGE_KEY = 'radiosuite-color-mode';
export const DEFAULT_COLOR_MODE = 'dark';

const darkBrand = {
  accent: '#22d3ee',
  accentDark: '#0891b2',
  onAir: '#ff3b3b',
  mixblock: '#c084fc',
  amber: '#f59e0b',
  surface1: '#11151d',
  surface2: '#161b25',
  surface3: '#1c2330',
};

const lightBrand = {
  accent: '#0891b2',
  accentDark: '#0e7490',
  onAir: '#e11d1d',
  mixblock: '#7c3aed',
  amber: '#d97706',
  surface1: '#eef2f7',
  surface2: '#ffffff',
  surface3: '#e8edf5',
};

export const brand = darkBrand;

export function normalizeColorMode(value) {
  return value === 'light' ? 'light' : 'dark';
}

export function readStoredColorMode() {
  if (typeof window === 'undefined') return DEFAULT_COLOR_MODE;
  try {
    return normalizeColorMode(window.localStorage.getItem(COLOR_MODE_STORAGE_KEY));
  } catch (_err) {
    return DEFAULT_COLOR_MODE;
  }
}

export function persistColorMode(mode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, normalizeColorMode(mode));
  } catch (_err) {
    // Ignore quota / private-mode failures.
  }
}

export function applyColorModeToDocument(mode) {
  if (typeof document === 'undefined') return;
  const resolved = normalizeColorMode(mode);
  document.documentElement.setAttribute('data-color-mode', resolved);
  document.documentElement.style.colorScheme = resolved;
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute('content', resolved === 'light' ? '#f3f5f9' : '#0c0e14');
  }
}

export function readThemeColor(variableName, fallback) {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  return value || fallback;
}

export function createAppTheme(mode = DEFAULT_COLOR_MODE) {
  const isDark = normalizeColorMode(mode) === 'dark';
  const paletteBrand = isDark ? darkBrand : lightBrand;

  const hairline = isDark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.08)';
  const hairlineStrong = isDark ? 'rgba(148,163,184,0.22)' : 'rgba(15,23,42,0.16)';

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#2563eb',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
      },
      success: {
        main: '#22c55e',
        light: '#4ade80',
        dark: '#16a34a',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
      },
      info: {
        main: '#22d3ee',
        light: '#67e8f9',
        dark: '#0891b2',
      },
      background: isDark
        ? { default: '#0a0c11', paper: '#161b25' }
        : { default: '#f3f5f9', paper: '#ffffff' },
      divider: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.12)',
      text: isDark
        ? { primary: '#eef2f8', secondary: '#9aa7b8', disabled: '#5b6675' }
        : { primary: '#0f172a', secondary: '#475569', disabled: '#94a3b8' },
      brand: paletteBrand,
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
      h4: { fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif', fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, letterSpacing: '0.01em' },
      overline: { fontWeight: 700, letterSpacing: '0.16em', fontSize: '0.68rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#0a0c11' : '#f3f5f9',
            // Flat base colour only — the animated aurora layer in index.css
            // (body::before) provides the background artwork.
            backgroundImage: 'none',
            minHeight: '100vh',
          },
          '::selection': {
            background: 'rgba(34,211,238,0.30)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 38%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 38%)',
            backgroundColor: isDark ? 'rgba(22,27,37,0.62)' : 'rgba(255,255,255,0.78)',
            backdropFilter: 'blur(14px) saturate(1.3)',
            WebkitBackdropFilter: 'blur(14px) saturate(1.3)',
            border: `1px solid ${hairline}`,
            borderRadius: 14,
            boxShadow: isDark
              ? '0 1px 0 rgba(255,255,255,0.05) inset, 0 16px 40px -18px rgba(0,0,0,0.75)'
              : '0 1px 0 rgba(255,255,255,0.8) inset, 0 16px 40px -22px rgba(15,23,42,0.20)',
            transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
            '&:hover': {
              borderColor: isDark ? 'rgba(148,163,184,0.22)' : 'rgba(15,23,42,0.14)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: hairline,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 9,
            boxShadow: 'none',
          },
          containedPrimary: {
            backgroundImage: 'linear-gradient(180deg, #4b91ff 0%, #2f6fe0 100%)',
            boxShadow: '0 4px 18px -6px rgba(59,130,246,0.55)',
            '&:hover': {
              backgroundImage: 'linear-gradient(180deg, #5a9bff 0%, #3b82f6 100%)',
              boxShadow: '0 0 28px -4px rgba(59,130,246,0.75), 0 8px 24px -8px rgba(59,130,246,0.6)',
            },
          },
          outlined: {
            borderColor: isDark ? 'rgba(148,163,184,0.28)' : 'rgba(15,23,42,0.18)',
            '&:hover': {
              borderColor: isDark ? 'rgba(148,163,184,0.5)' : 'rgba(15,23,42,0.32)',
              backgroundColor: isDark ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.04)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: hairline,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(148,163,184,0.10)' : 'rgba(15,23,42,0.08)',
          },
          head: {
            backgroundColor: isDark ? 'rgba(148,163,184,0.05)' : 'rgba(15,23,42,0.04)',
            color: isDark ? '#9aa7b8' : '#475569',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(148,163,184,0.04)' : 'rgba(15,23,42,0.04)',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 3,
            backgroundImage: 'linear-gradient(90deg, #22d3ee, #3b82f6)',
            boxShadow: isDark ? '0 0 12px rgba(34,211,238,0.7)' : 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 46,
            '&.Mui-selected': {
              color: isDark ? '#67e8f9' : '#0891b2',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#0f1420' : '#0f172a',
            border: isDark ? '1px solid rgba(148,163,184,0.18)' : '1px solid rgba(15,23,42,0.12)',
            fontSize: '0.75rem',
            fontWeight: 500,
            boxShadow: isDark
              ? '0 8px 24px -8px rgba(0,0,0,0.8)'
              : '0 8px 24px -8px rgba(15,23,42,0.28)',
          },
          arrow: { color: isDark ? '#0f1420' : '#0f172a' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 9,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: hairlineStrong,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(148,163,184,0.4)' : 'rgba(15,23,42,0.28)',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))'
              : 'none',
            backgroundColor: isDark ? 'rgba(22,27,37,0.88)' : 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(20px) saturate(1.3)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.3)',
            borderRadius: 16,
            border: `1px solid ${hairline}`,
            boxShadow: isDark
              ? '0 24px 80px -24px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.03)'
              : '0 24px 80px -32px rgba(15,23,42,0.35)',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 9999,
            backgroundColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.10)',
          },
          bar: {
            borderRadius: 9999,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          rounded: { borderRadius: 12 },
        },
      },
    },
  });
}
